import { useI18n } from '../../lib/i18n'
import { OUTCOMES, type Outcome } from '../../upload/outcome'
import './uploads.css'

type Counts = Readonly<Partial<Record<Outcome, number>>>

/**
 * The files by outcome as one bar. Never by colour alone (NFR-08): each part has a pattern and its
 * name, and the tiles beside it give the counts.
 */
export function OutcomeBar({ counts, slim }: { counts: Counts; slim?: boolean }) {
  const { t, lang } = useI18n()
  const parts = OUTCOMES.filter((outcome) => (counts[outcome] ?? 0) > 0)
  const label = `${t('filesByOutcome')}: ${parts.map((outcome) => `${t(`outcome_${outcome}`)} ${counts[outcome]!.toLocaleString(lang)}`).join(', ')}`
  return (
    <div className={slim ? 'outcome-bar slim' : 'outcome-bar'} role="img" aria-label={label}>
      {parts.map((outcome) => (
        <span key={outcome} className={`outcome-${outcome}`} style={{ flexGrow: counts[outcome] }} />
      ))}
    </div>
  )
}

/** The counts per outcome; choosing one shows its files, choosing it again shows every problem. */
export function OutcomeTiles({
  counts,
  selected,
  onSelect,
}: {
  counts: Counts
  selected: Outcome | null
  onSelect: (outcome: Outcome | null) => void
}) {
  const { t, lang } = useI18n()
  return (
    <div className="outcome-tiles">
      {OUTCOMES.filter((outcome) => (counts[outcome] ?? 0) > 0).map((outcome) => (
        <button
          key={outcome}
          type="button"
          className="outcome-tile"
          aria-pressed={selected === outcome}
          onClick={() => onSelect(selected === outcome ? null : outcome)}
        >
          <span className="outcome-name">
            <span className={`outcome-swatch outcome-${outcome}`} aria-hidden="true" />
            {t(`outcome_${outcome}`)}
          </span>
          <span className="outcome-count">{counts[outcome]!.toLocaleString(lang)}</span>
        </button>
      ))}
    </div>
  )
}
